/**
 * Cookie Manager & Consent Enforcement Engine
 * Manages user cookie preferences, version validation, script execution gating,
 * and cleaning of rejected tracking cookies.
 */

export const COOKIE_POLICY_VERSION = "1.0.0";
export const COOKIE_STORAGE_KEY = "entelipro_cookie_preferences";

export const COOKIE_CATEGORIES = {
  ESSENTIAL: "essential",
  ANALYTICS: "analytics",
  FUNCTIONAL: "functional",
};

export const DEFAULT_CONSENT_STATE = {
  version: COOKIE_POLICY_VERSION,
  timestamp: null,
  consentStatus: null, // "accepted_all" | "rejected_all" | "custom" | null
  categories: {
    essential: true, // Always required and locked
    analytics: false, // Default false until explicit consent
    functional: false, // Default false until explicit consent
  },
};

/**
 * Common tracking cookies to delete when analytics consent is rejected or revoked.
 */
const KNOWN_TRACKING_COOKIES = [
  "_ga",
  "_gid",
  "_gat",
  "_gcl_au",
  "_gcl_aw",
  "_fbp",
  "hubspotutk",
  "__hstc",
  "__hssc",
  "__hssrc",
  "_pk_id",
  "_pk_ses",
  "_clck",
  "_clsk",
];

/**
 * Delete a specific cookie across common domain variations and paths.
 */
export const deleteCookie = (name) => {
  const host = window.location.hostname;
  const domainParts = host.split(".");
  const domains = [
    host,
    `.${host}`,
    ...(domainParts.length >= 2 ? [`.${domainParts.slice(-2).join(".")}`] : []),
  ];

  const paths = ["/", window.location.pathname];

  domains.forEach((d) => {
    paths.forEach((p) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${p}; domain=${d};`;
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${p};`;
    });
  });
};

/**
 * Clean all recognized tracking cookies if Analytics consent is denied.
 */
export const cleanRejectedCookies = () => {
  try {
    const cookies = document.cookie ? document.cookie.split(";") : [];
    cookies.forEach((cookieStr) => {
      const cookieName = cookieStr.split("=")[0].trim();
      const isTracking =
        KNOWN_TRACKING_COOKIES.some((prefix) => cookieName.startsWith(prefix)) ||
        cookieName.startsWith("_ga_");
      if (isTracking) {
        deleteCookie(cookieName);
      }
    });
  } catch (err) {
    console.warn("[CookieManager] Failed cleaning tracking cookies:", err);
  }
};

/**
 * Get stored consent from localStorage with version validation.
 * Returns null if no consent exists or if version is outdated.
 */
export const getStoredConsent = () => {
  try {
    const raw = localStorage.getItem(COOKIE_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== COOKIE_POLICY_VERSION) {
      // Invalidate outdated consent
      return null;
    }
    return parsed;
  } catch (e) {
    return null;
  }
};

/**
 * Check if a specific cookie category is permitted.
 * Strict default: False until explicit user consent is given.
 */
export const isCategoryAllowed = (category) => {
  if (category === COOKIE_CATEGORIES.ESSENTIAL) return true;

  const stored = getStoredConsent();
  if (!stored || !stored.categories) return false;

  return Boolean(stored.categories[category]);
};

/**
 * Save user cookie consent preferences.
 */
export const saveConsent = (categories, consentStatus = "custom") => {
  const payload = {
    version: COOKIE_POLICY_VERSION,
    timestamp: new Date().toISOString(),
    consentStatus,
    categories: {
      essential: true, // Non-negotiable
      analytics: Boolean(categories?.analytics),
      functional: Boolean(categories?.functional),
    },
  };

  try {
    localStorage.setItem(COOKIE_STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    console.warn("[CookieManager] Could not save to localStorage:", err);
  }

  // Enforce cleanup if analytics is disabled
  if (!payload.categories.analytics) {
    cleanRejectedCookies();
  }

  // Notify active listeners across the app
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("cookieConsentChanged", { detail: payload })
    );
  }

  return payload;
};

/**
 * Convenience helper: Accept all cookie categories
 */
export const acceptAll = () => {
  return saveConsent(
    { essential: true, analytics: true, functional: true },
    "accepted_all"
  );
};

/**
 * Convenience helper: Decline all optional categories
 */
export const declineAll = () => {
  return saveConsent(
    { essential: true, analytics: false, functional: false },
    "rejected_all"
  );
};

/**
 * Conditional script runner / gatekeeper.
 * Executes callback only if category is granted, or when granted in the future.
 */
export const runWhenAllowed = (category, callback) => {
  if (isCategoryAllowed(category)) {
    callback();
    return () => {};
  }

  const listener = (event) => {
    if (event?.detail?.categories?.[category]) {
      callback();
      window.removeEventListener("cookieConsentChanged", listener);
    }
  };

  window.addEventListener("cookieConsentChanged", listener);
  return () => window.removeEventListener("cookieConsentChanged", listener);
};

// Expose safe global helpers for any external/inline scripts
if (typeof window !== "undefined") {
  window.__isCookieCategoryAllowed = isCategoryAllowed;
  window.__getCookieConsent = getStoredConsent;
}
